// src/pwa/src/services/whatsappService.ts
// Issue 74 — WhatsApp transport. Tries the Cloudflare WhatsApp worker endpoint
// first so the PDF can be uploaded to the chat as a real document; falls back
// to wa.me with a link to the saved-to-repo PDF URL.

import { savePdfToRepo } from './documentsRepoService';

export interface SendWhatsAppArgs {
    phone?: string; // E.164 without +, e.g. "27721234567"
    body: string;
    pdfLink?: { filename: string; pdf: Blob };
}

export interface SendWhatsAppResult {
    delivered: boolean;
    method: 'wa-me' | 'api';
    fallbackUri?: string;
    pdfUrl?: string;
}

function normalizePhone(input?: string): string | undefined {
    if (!input) {return undefined;}
    const digits = String(input).replace(/[^0-9]/g, '');
    if (digits.length < 7) {return undefined;}
    return digits.replace(/^0/, '27'); // ZA fallback
}

export async function sendWhatsApp(args: SendWhatsAppArgs): Promise<SendWhatsAppResult> {
    const phone = normalizePhone(args.phone);

    let pdfUrl: string | undefined;
    if (args.pdfLink) {
        try {
            const saved = await savePdfToRepo({
                type: 'invoice', // generic bucket; transport adds Info in body
                reference: args.pdfLink.filename.replace(/\.pdf$/i, ''),
                pdf: args.pdfLink.pdf,
            });
            pdfUrl = saved.url;
        } catch {
            pdfUrl = undefined;
        }
    }

    try {
        const res = await fetch('/api/whatsapp/send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                phone,
                message: pdfUrl ? `${args.body}\n\n📎 ${args.pdfLink?.filename}: ${pdfUrl}` : args.body,
                pdfUrl,
            }),
        });
        if (res.ok) {
            return { delivered: true, method: 'api', pdfUrl };
        }
    } catch {
        // fall through to wa.me
    }

    const msg = pdfUrl ? encodeURIComponent(`${args.body}\n\n📎 ${args.pdfLink?.filename}: ${pdfUrl}`) : encodeURIComponent(args.body);
    const uri = phone
        ? `https://wa.me/${phone}?text=${msg}`
        : `https://wa.me/?text=${msg}`;
    window.open(uri, '_blank');
    return { delivered: true, method: 'wa-me', fallbackUri: uri, pdfUrl };
}
